// =============================================================================
// 분류 엔진 — LLM 분류 클라이언트 (OpenAI 호환 Chat Completions, 설정 주입식)
// designs/erp-mail-auto-classify-v2.md §5.0/§5.1-c
//
// 도메인 무관: 후보 카탈로그(라벨·힌트·few-shot 사례)와 콘텐츠만 받아 targetKey 를
// 판정한다. env 를 직접 읽지 않는다 — apiKey/model 은 소비자가 주입.
// =============================================================================
import { requestJsonCompletion } from '../llm/json-completion.js';
/** LLM 에 넘길 본문 발췌 상한. */
const BODY_EXCERPT_MAX = 2000;
/** 후보 상한 — 프롬프트 폭주 방지. */
export const LLM_CANDIDATE_MAX = 40;
const verdictSchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        targetKey: {
            type: ['string', 'null'],
            description: '후보 목록의 targetKey 중 하나. 확신이 없거나 무관하면 null.',
        },
        confidence: {
            type: 'number',
            description: '0..1 확신도. 명시적 근거가 없으면 0.5 미만.',
        },
        reason: {
            type: 'string',
            description: '판단 근거 한 문장(한국어). targetKey 값(예: p:xxxx)은 사람이 읽을 수 없으니 절대 인용하지 말고, 후보의 라벨(이름)로만 언급한다.',
        },
    },
    required: ['targetKey', 'confidence', 'reason'],
};
/** senderKind 활성 시의 확장 스키마 — strict 라 required 에 포함(null 허용). */
const verdictSchemaWithSenderKind = {
    ...verdictSchema,
    properties: {
        ...verdictSchema.properties,
        senderKind: {
            type: ['string', 'null'],
            enum: ['AD', 'NOTICE', 'SYSTEM', null],
            description: '발신 성격: AD=광고/마케팅, NOTICE=기관·단체의 단순 안내(수신자 업무와 직접 무관), SYSTEM=플랫폼 자동 알림(가입·보안·빌드 등). 업무 관련이면 null.',
        },
    },
    required: [...verdictSchema.required, 'senderKind'],
};
/** 후보 카탈로그 텍스트 — 힌트·few-shot 사례 포함. */
export function buildCandidateCatalog(candidates) {
    return candidates
        .slice(0, LLM_CANDIDATE_MAX)
        .map((c) => {
        const parts = [`- targetKey=${c.targetKey} | ${c.label}`];
        if (c.hints?.length)
            parts.push(`  힌트: ${c.hints.join(', ')}`);
        if (c.examples?.length) {
            parts.push(`  확정 사례: ${c.examples.map((e) => `"${e}"`).join(', ')}`);
        }
        return parts.join('\n');
    })
        .join('\n');
}
/**
 * LLM 분류 호출.
 * @param priorLines 소비자가 아는 사전 정보 (예: "이 상대의 과거 확정 분류: X 4건").
 */
export async function classifyWithLlm(config, content, candidates, priorLines = []) {
    const kind = config.contentKind ?? '콘텐츠';
    const outputText = await requestJsonCompletion({
        apiKey: config.apiKey,
        model: config.model,
        ...(config.baseUrl ? { baseUrl: config.baseUrl } : {}),
        timeoutMs: config.timeoutMs ?? 30_000,
    }, {
        messages: [
            {
                role: 'system',
                content: [
                    `너는 분류기다. ${kind} 1건이 아래 후보 중 어느 것에 관한 것인지 판단한다.`,
                    '',
                    '후보:',
                    buildCandidateCatalog(candidates),
                    ...(priorLines.length ? ['', '사전 정보:', ...priorLines] : []),
                    '',
                    '규칙:',
                    '- 제목·본문·상대 주소·첨부 파일명에서 후보의 라벨/힌트/사례와의 명시적 근거를 찾는다.',
                    '- 근거가 명확할 때만 해당 targetKey 와 confidence>=0.7 을 반환한다.',
                    '- 어느 정도 그럴듯하지만 확신이 부족하면 targetKey 를 채우되 confidence 0.4~0.7.',
                    '- 광고·뉴스레터·무관한 안내면 targetKey=null.',
                    '- reason 에는 targetKey 를 쓰지 않는다 — 후보는 라벨(이름)로만 언급한다.',
                    ...(config.classifySenderKind
                        ? [
                            '- senderKind: 광고(AD)/기관 단순안내(NOTICE)/플랫폼 자동알림(SYSTEM)이면 표시, 업무 관련이면 null.',
                        ]
                        : []),
                ].join('\n'),
            },
            {
                role: 'user',
                content: [
                    `제목: ${content.subject}`,
                    `상대 주소: ${content.counterparts.join(', ') || '(없음)'}`,
                    ...(content.attachmentNames?.length
                        ? [`첨부: ${content.attachmentNames.join(', ')}`]
                        : []),
                    '',
                    '본문 발췌:',
                    content.bodyExcerpt.slice(0, BODY_EXCERPT_MAX),
                ].join('\n'),
            },
        ],
        schemaName: 'classification_verdict',
        schema: config.classifySenderKind ? verdictSchemaWithSenderKind : verdictSchema,
    });
    return parseVerdict(outputText, candidates);
}
function parseVerdict(outputText, candidates) {
    const parsed = JSON.parse(outputText);
    const confidence = typeof parsed.confidence === 'number' && Number.isFinite(parsed.confidence)
        ? Math.min(1, Math.max(0, parsed.confidence))
        : 0;
    return {
        targetKey: normalizeTargetKey(typeof parsed.targetKey === 'string' && parsed.targetKey ? parsed.targetKey : null, candidates),
        confidence,
        reason: typeof parsed.reason === 'string' ? parsed.reason : '',
        senderKind: parsed.senderKind === 'AD' || parsed.senderKind === 'NOTICE' || parsed.senderKind === 'SYSTEM'
            ? parsed.senderKind
            : null,
    };
}
/**
 * LLM 이 답한 키를 후보 카탈로그로 정규화한다.
 * 모델이 접두사('p:' 등)를 떼거나 따옴표를 붙여 답하는 사례가 실측됨 —
 * 정확 일치 → 접미 일치(':<raw>') → 라벨 정확 일치 순으로 복원, 실패 시 null.
 */
function normalizeTargetKey(raw, candidates) {
    if (!raw)
        return null;
    const key = raw.trim().replace(/^['"]|['"]$/g, '');
    if (candidates.some((c) => c.targetKey === key))
        return key;
    const bySuffix = candidates.find((c) => c.targetKey.endsWith(`:${key}`));
    if (bySuffix)
        return bySuffix.targetKey;
    const byLabel = candidates.find((c) => c.label === key);
    return byLabel ? byLabel.targetKey : null;
}
//# sourceMappingURL=llm.js.map